import {ChangeDetectionStrategy, Component, inject, OnInit, signal} from '@angular/core';
import {DocumentsService} from '../../documents/services/documents.service';
import {SessionStorageService} from '@shared/services/session-storage.service';
import {ClientsideDataService} from '@shared/services/clientside-data.service';
import {DocumentViewModel} from '../../documents/models/document.view-model';
import {DocumentConverterService} from '../../documents/services/document-converter.service';
import {TableTemplateComponentHost} from '@components/ui/table-template/table-template-component-host';
import {SortedTable} from '@shared/enums/sorted-table.enum';
import {TableTemplateColumnConfig} from '@components/ui/table-template/models/table-template-column-config';
import {DataViewModel} from '@components/ui/table-template/models/data-view-model';
import {CellType} from '@components/ui/table-template/enums/cell-type.enum';
import {Alignment} from '@components/ui/table-template/enums/alignment.enum';
import {CellClicked} from '@components/ui/table-template/models/cell-clicked';
import {saveAs} from 'file-saver';
import {PageViewModel} from '@components/ui/table-template/models/page-view-model';
import {AppParameterMap} from '../../config/app-parameter-map';
import {PARAM_ENTITY_ID} from '../../config/app-urls';
import {Router} from '@angular/router';
import {Keys} from '@shared/keys';
import {LoadingAnimationComponent} from '@shared/components/loading-animation/loading-animation.component';
import {TableTemplateComponent} from '@components/ui/table-template/table-template.component';
import {EntityDocumentCardComponent} from './entity-document-card/entity-document-card.component';

@Component({
  selector: 'app-client-documents',
  templateUrl: './client-documents.component.html',
  styleUrls: ['./client-documents.component.scss'],
  providers: [ClientsideDataService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LoadingAnimationComponent, TableTemplateComponent, EntityDocumentCardComponent]
})
export class ClientDocumentsComponent implements OnInit, TableTemplateComponentHost {

  private readonly router = inject(Router);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly documentService = inject(DocumentsService);
  private readonly clientSideDataService = inject(ClientsideDataService<DocumentViewModel>);
  private readonly documentConverter = inject(DocumentConverterService);

  readonly dataLoading = signal(true);
  readonly dataAvailable = signal(false);
  readonly dataEmpty = signal(false);
  readonly columns = signal<TableTemplateColumnConfig[]>([]);
  readonly sortField = signal('uploadedDateTime');
  readonly sortOrder = signal(-1);
  readonly searchString = signal('');
  readonly pageNumber = signal(1);
  readonly pageSize = signal(20);
  readonly currentPage = signal<PageViewModel<DocumentViewModel>>({
    totalElements: 0,
    pageNumber: 1,
    pageSize: 20,
    data: []
  });
  private readonly SEARCH_FIELDS: Keys<DocumentViewModel> = ['documentTitle'];

  readonly clientDocuments = signal<DocumentViewModel[]>([]);

  ngOnInit(): void {
    this.dataLoading.set(true);
    this.dataEmpty.set(false);
    this.dataAvailable.set(false);

    this.populateColumns();
    const storedSort = this.sessionStorageService.getSort(SortedTable.Documents);
    if (typeof storedSort[0] === 'string' && typeof storedSort[1] === 'number') {
      this.sortField.set(storedSort[0]);
      this.sortOrder.set(storedSort[1]);
    }

    const params = AppParameterMap.from(this.router);
    const entityId = params.get(PARAM_ENTITY_ID);

    this.documentService.getClientDocuments(entityId).subscribe(documents => {
      const docs = this.documentConverter.convert(documents);
      this.clientDocuments.set(docs);
      this.clientSideDataService.initialize(docs);
      this.updateData();
      this.dataLoading.set(false);
      this.dataEmpty.set(docs.length === 0);
      this.dataAvailable.set(!this.dataEmpty());
    });
  }

  sort(column: TableTemplateColumnConfig): void {
    this.updateSortColumn(column.dataProperty);
    this.pageNumber.set(1);
    this.updateData();
  }

  search(searchTerm: string): void {
    this.searchString.set(searchTerm);
    this.pageNumber.set(1);
    this.updateData();
  }

  page(targetPage: number): void {
    this.pageNumber.set(targetPage);
    this.updateData();
  }

  rowClicked(_dataViewModel: DataViewModel): void {
    // Row clicking is not supported in this view
  }

  populateColumns() {
    this.columns.set([
      {
        title: 'Title',
        dataProperty: 'documentTitle',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Left,
        width: '30%',
        dataFocusable: true,
        headingFocusable: true,
        emptyFallback: '(Empty)'
      },
      {
        title: 'File Format',
        dataProperty: 'fileExtension',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Left,
        width: '20%',
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: 'Date and Time',
        dataProperty: 'uploadedDateTime',
        cellType: CellType.DateTime,
        sortable: true,
        alignment: Alignment.Left,
        width: '15%',
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: '',
        dataProperty: 'downloadButtonText',
        cellType: CellType.Button,
        sortable: false,
        alignment: Alignment.Center,
        width: '35%',
        dataFocusable: false,
        headingFocusable: false
      }
    ]);
  }


  cellClicked(cellClicked: CellClicked) {
    const documentVM = cellClicked.row as DocumentViewModel;
    this.downloadDocument(documentVM);
  }

  downloadDocument(documentVM: DocumentViewModel) {
    this.documentService.getClientDocument(documentVM.clientCode, documentVM.entryNo).subscribe(documentBlob => {
      this.saveFile(documentBlob, documentVM.fileDownloadName);
    });
  }

  saveFile(blob: Blob, fileName: string): void {
    saveAs(blob, fileName);
  }

  private updateSortColumn(field: string): void {
    if (this.sortField() === field) {
      this.sortOrder.set(this.sortOrder() * -1);
    } else {
      this.sortField.set(field);
      this.sortOrder.set(1);
    }

    this.sessionStorageService.setSort(SortedTable.Documents, this.sortField(), this.sortOrder());
  }

  private updateData(): void {
    this.currentPage.set(this.clientSideDataService.generatePage(
      this.sortField(),
      this.sortOrder() === 1,
      this.SEARCH_FIELDS,
      this.searchString(),
      this.pageNumber(),
      this.pageSize()
    ));
  }
}
